import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';

const ConsultationCompletionForm = ({ session, open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    consultationDuration: '',
    serviceProvided: false,
    referralConfirmed: false,
    consultationCompleted: false,
    clinicalNotes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patientName, setPatientName] = useState(null);

  const durationOptions = [
    { value: '0', label: '0 minutes' },
    { value: '25', label: '25 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '35', label: '35 minutes' },
    { value: '40', label: '40 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '50', label: '50 minutes' },
    { value: '55', label: '55 minutes' },
    { value: '60', label: '60+ minutes' }
  ];

  useEffect(() => {
    if (open && session) {
      // Reset form when modal opens
      setFormData({
        consultationDuration: '',
        serviceProvided: false,
        referralConfirmed: false,
        consultationCompleted: false,
        clinicalNotes: ''
      });
      setErrors({});
      
      // Try to get patient name
      if (session.patient?.name) {
        setPatientName(session.patient.name);
        console.log('✅ Using session.patient.name:', session.patient.name);
      } else if (session.patientName) {
        setPatientName(session.patientName);
        console.log('✅ Using session.patientName:', session.patientName);
      } else if (session.patientId && typeof session.patientId === 'string') {
        // We only have patientId, try to fetch patient name
        console.log('🔄 Only patientId available, fetching patient name for:', session.patientId);
        fetchPatientName(session.patientId);
      } else {
        console.log('❌ No patient data available in session');
        setPatientName('Patient Name Not Available');
      }
    }
  }, [open, session]);

  // Function to fetch patient name by ID
  const fetchPatientName = async (patientId) => {
    try {
      setPatientName('Loading...');
      
      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      
      // Try multiple possible endpoints
      const possibleEndpoints = [
        `/api/users/${patientId}`,
        `/api/admin/users/${patientId}`,
        `/api/users/profile/${patientId}`
      ];
      
      let userData = null;
      
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`🔍 Trying endpoint: ${endpoint}`);
          const response = await fetch(endpoint, { headers });
          
          if (response.ok) {
            userData = await response.json();
            console.log('✅ Successfully fetched user data:', userData);
            break;
          } else {
            console.log(`❌ Endpoint ${endpoint} failed with status:`, response.status);
          }
        } catch (endpointError) {
          console.log(`❌ Endpoint ${endpoint} error:`, endpointError.message);
        }
      }

      if (userData) {
        // Try different possible name fields
        const name = userData.name || 
                    userData.user?.name || 
                    userData.data?.name ||
                    (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : userData.firstName) ||
                    'Unknown Patient';
        setPatientName(name);
      } else {
        console.log('❌ All endpoints failed, using patientId as fallback');
        setPatientName(`Patient (ID: ${patientId.slice(-6)})`);
      }
      
    } catch (error) {
      console.error('❌ Error fetching patient name:', error);
      setPatientName(`Patient (ID: ${patientId.slice(-6)})`);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Duration validation
    if (!formData.consultationDuration) {
      newErrors.consultationDuration = 'Please select a consultation duration';
    } else if (parseInt(formData.consultationDuration) < 20) {
      newErrors.consultationDuration = 'Consultation duration must be at least 20 minutes to submit';
    }

    // Checkbox validations
    if (!formData.serviceProvided) {
      newErrors.serviceProvided = 'This verification is required';
    }
    if (!formData.referralConfirmed) {
      newErrors.referralConfirmed = 'This verification is required';
    }
    if (!formData.consultationCompleted) {
      newErrors.consultationCompleted = 'This verification is required';
    }

    // Validate service date
    const serviceDate = session?.date || new Date().toISOString().split('T')[0];
    try {
      let parsedDate;
      
      // Handle different date formats
      if (typeof serviceDate === 'string') {
        // Try parsing as is first
        parsedDate = new Date(serviceDate);
        
        // If invalid, try adding time component
        if (isNaN(parsedDate.getTime())) {
          parsedDate = new Date(serviceDate + 'T00:00:00.000Z');
        }
      } else if (serviceDate instanceof Date) {
        parsedDate = serviceDate;
      } else {
        // Use current date as fallback
        parsedDate = new Date();
      }
      
      if (isNaN(parsedDate.getTime())) {
        newErrors.serviceDate = 'Invalid service date';
      } else {
        // Check if date is not too far in the future
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (parsedDate > tomorrow) {
          newErrors.serviceDate = 'Service date cannot be in the future';
        }
      }
    } catch (dateError) {
      console.error('Date validation error:', dateError);
      newErrors.serviceDate = 'Invalid service date format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts correcting
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Format the actual service date properly with better fallbacks
      let actualServiceDate;
      
      if (session?.date) {
        // Use session date if available
        if (typeof session.date === 'string') {
          actualServiceDate = session.date;
        } else if (session.date instanceof Date) {
          actualServiceDate = session.date.toISOString().split('T')[0];
        } else {
          actualServiceDate = new Date().toISOString().split('T')[0];
        }
      } else {
        // Fallback to current date
        actualServiceDate = new Date().toISOString().split('T')[0];
      }
      
      console.log('📅 Submitting with service date:', actualServiceDate);
      
      await onSubmit({
        sessionId: session.id || session._id,
        ...formData,
        actualServiceDate: actualServiceDate
      });
    } catch (error) {
      console.error('Failed to submit consultation completion:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDateTime = (date, time) => {
    try {
      if (!date || !time) {
        // Try to use session data if available
        if (session?.date && session?.startTime) {
          return formatDateTime(session.date, session.startTime);
        }
        return 'Date/Time not available';
      }
      
      // Handle different date formats
      let dateToFormat;
      if (typeof date === 'string') {
        // If date is in YYYY-MM-DD format, use it directly
        dateToFormat = date;
      } else if (date instanceof Date) {
        dateToFormat = date.toISOString().split('T')[0];
      } else {
        return 'Invalid date format';
      }
      
      // Handle different time formats
      let timeToFormat = time;
      if (typeof time !== 'string') {
        return 'Invalid time format';
      }
      
      // Clean up time format - handle "5:40 PM" format directly
      timeToFormat = timeToFormat.trim();
      
      // Try to create a date object using a more reliable method
      let dateObj;
      
      if (timeToFormat.includes('AM') || timeToFormat.includes('PM')) {
        // Time is already in 12hr format like "5:40 PM"
        // Create a full date string for parsing
        const dateTimeStr = `${dateToFormat} ${timeToFormat}`;
        dateObj = new Date(dateTimeStr);
        
        // If that fails, try a different approach
        if (isNaN(dateObj.getTime())) {
          // Parse manually
          const [timePart, ampm] = timeToFormat.split(/\s+(AM|PM)/i);
          const [hours, minutes] = timePart.split(':');
          let hour24 = parseInt(hours);
          
          if (ampm.toUpperCase() === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (ampm.toUpperCase() === 'AM' && hour24 === 12) {
            hour24 = 0;
          }
          
          dateObj = new Date(`${dateToFormat}T${hour24.toString().padStart(2, '0')}:${minutes}:00`);
        }
      } else {
        // Assume 24hr format, convert to proper date
        dateObj = new Date(`${dateToFormat}T${timeToFormat}:00`);
      }
      
      // Check if the date is valid
      if (isNaN(dateObj.getTime())) {
        // Fallback: just display the raw date and time
        return `${dateToFormat} at ${timeToFormat}`;
      }
      
      return formatValidDate(dateObj);
      
    } catch (error) {
      console.error('Error formatting date/time:', error, { date, time });
      // Fallback: display raw values
      return `${date} at ${time}`;
    }
  };

  const formatValidDate = (dateObj) => {
    const formattedDate = dateObj.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
    
    const formattedTime = dateObj.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
    
    return `${formattedDate} • ${formattedTime}`;
  };

  // Helper function to get patient name
  const getPatientName = () => {
    // Use the state value if available
    if (patientName) {
      return patientName;
    }
    
    // Fallback to immediate session data parsing
    if (session?.patient?.name) {
      return session.patient.name;
    }
    if (session?.patientName) {
      return session.patientName;
    }
    if (session?.patient?.firstName && session?.patient?.lastName) {
      return `${session.patient.firstName} ${session.patient.lastName}`;
    }
    if (session?.patient?.firstName) {
      return session.patient.firstName;
    }
    
    // If we only have patientId (ObjectId string), show loading message
    if (session?.patientId && typeof session.patientId === 'string') {
      return 'Loading Patient Name...';
    }
    
    return 'Patient Name Not Available';
  };

  if (!open || !session) return null;

  return (
    <>
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[calc(100vh-2rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex-1 pr-2">
            <h2 className="text-lg font-semibold text-gray-800">
              Consultation Completion Form
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Complete this form to finalize the session and trigger Medicare receipt generation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0 custom-scrollbar" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#9CA3AF #F3F4F6'
        }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Patient Information */}
            <div className="bg-gray-50 rounded-lg p-3">
              <h3 className="font-medium text-gray-800 mb-1 text-sm">{getPatientName()}</h3>
              <p className="text-sm text-gray-600">
                {formatDateTime(session.date, session.startTime)}
              </p>
              
              {/* Show service date error if exists */}
              {errors.serviceDate && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{errors.serviceDate}</p>
                </div>
              )}
              
              {/* Status Indicators */}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Pending Completion</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-gray-600">Zoom Ending</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Post Zoom Meeting</span>
                </div>
              </div>
            </div>

            {/* Consultation Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consultation Duration *
              </label>
              <select
                value={formData.consultationDuration}
                onChange={(e) => handleInputChange('consultationDuration', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.consultationDuration ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select duration</option>
                {durationOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.consultationDuration && (
                <p className="text-red-600 text-sm mt-1">{errors.consultationDuration}</p>
              )}
            </div>

            {/* Verification Checkboxes */}
            <div className="space-y-3">
              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.serviceProvided}
                    onChange={(e) => handleInputChange('serviceProvided', e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Service provided under an Allied GP referral
                    </span>
                    {errors.serviceProvided && (
                      <p className="text-red-600 text-xs mt-1">{errors.serviceProvided}</p>
                    )}
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.referralConfirmed}
                    onChange={(e) => handleInputChange('referralConfirmed', e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Referral details confirmed verbally with patient during video call
                    </span>
                    {errors.referralConfirmed && (
                      <p className="text-red-600 text-xs mt-1">{errors.referralConfirmed}</p>
                    )}
                  </div>
                </label>
              </div>

              <div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={formData.consultationCompleted}
                    onChange={(e) => handleInputChange('consultationCompleted', e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      Telehealth consultation completed
                    </span>
                    {errors.consultationCompleted && (
                      <p className="text-red-600 text-xs mt-1">{errors.consultationCompleted}</p>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Clinical Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clinical Notes (Optional)
              </label>
              <textarea
                value={formData.clinicalNotes}
                onChange={(e) => handleInputChange('clinicalNotes', e.target.value)}
                placeholder="Enter any notes or comments about this session..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Processing...' : 'Complete Session & Generate Receipt'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ConsultationCompletionForm; 